/* ===================================================================
   useMe() -- the signed-in client's identity context (Brad, 2026-06-17).

   GET /api/v1/me -> { client_id, business_name, status, tier }. Used to
   feed googleConnectUrl(client_id) on the Integrations panel and to label
   the real business in the shell. Disabled until a Bearer rr_ token exists
   so it never auto-fires a 401 on first paint.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type AdminTenantDashboard, type MeResponse } from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { adminDashboardKey } from './useAdminTenantDashboard';
import { qk } from './queryKeys';

export function useMe(): UseQueryResult<MeResponse, Error> {
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, select the identity slice out of
  // the single admin dashboard bundle (so the goal ring + any business labels
  // resolve to the VIEWED tenant, not the operator's own token).
  return useQuery({
    queryKey: admin ? adminDashboardKey(admin.clientId) : qk.me(),
    queryFn: ({ signal }) =>
      admin
        ? (api.getTenantDashboard(admin.clientId, signal) as Promise<unknown>)
        : (api.me(signal) as Promise<unknown>),
    select: admin ? (d) => (d as AdminTenantDashboard).identity : undefined,
    enabled: admin ? true : hasToken,
    staleTime: 5 * 60_000,
  }) as UseQueryResult<MeResponse, Error>;
}
